import React, { useState, useEffect, useCallback } from 'react';
import { resumeService, ActivityLogItem } from '../../services/api';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, RefreshCw, CheckCircle2, XCircle, Filter } from 'lucide-react';

const PAGE_SIZE = 50;

const AdminActivityTab: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLogItem[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('');
  const [featureFilter, setFeatureFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'error'>('all');
  const [selectedLog, setSelectedLog] = useState<ActivityLogItem | null>(null);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const successFilter = statusFilter === 'all' ? undefined : statusFilter === 'success';
      const res = await resumeService.getAdminActivityLogs(
        page,
        PAGE_SIZE,
        eventFilter || undefined,
        featureFilter || undefined,
        successFilter
      );
      const content = res.data.content ?? [];
      setLogs(content);
      setTotal(res.data.total ?? 0);
      setTotalPages(res.data.totalPages ?? 0);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setIsLoading(false);
    }
  }, [page, eventFilter, featureFilter, statusFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const shortUser = (id: string) => (id ? `${id.slice(0, 8)}…` : '—');

  return (
    <>
      <div className="bg-white p-4 rounded-card mb-6 shadow-soft">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-4 h-4 text-secondary-foreground" />
          <input
            type="text"
            placeholder="Filter by event..."
            value={eventFilter}
            onChange={(e) => { setEventFilter(e.target.value); setPage(0); }}
            className="flex-1 min-w-[140px] h-10 px-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
          />
          <input
            type="text"
            placeholder="Filter by feature..."
            value={featureFilter}
            onChange={(e) => { setFeatureFilter(e.target.value); setPage(0); }}
            className="flex-1 min-w-[140px] h-10 px-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as 'all' | 'success' | 'error'); setPage(0); }}
            className="h-10 px-3 text-sm border border-zinc-200 rounded-btn text-foreground bg-white"
          >
            <option value="all">All statuses</option>
            <option value="success">Success</option>
            <option value="error">Errors</option>
          </select>
          <button
            onClick={() => { setPage(0); fetchLogs(); }}
            className="h-10 px-4 bg-white border border-zinc-200 rounded-btn text-sm text-foreground hover:bg-subtle inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
        <p className="mt-2 text-xs text-secondary-foreground">
          {isLoading ? 'Loading…' : `Showing ${logs.length} of ${total} total events (page ${page + 1} of ${Math.max(totalPages, 1)})`}
        </p>
      </div>

      {isLoading ? (
        <div className="text-center p-12 text-secondary-foreground">Loading activity logs...</div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-card shadow-soft text-center p-12 text-secondary-foreground">
          No activity found
        </div>
      ) : (
        <div className="bg-white rounded-card shadow-soft">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse [&_tr:last-child_td]:border-b-0">
              <thead>
                <tr>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Time</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">User</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Event</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Feature</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Path</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-subtle cursor-pointer" onClick={() => setSelectedLog(log)}>
                    <td className="p-4 text-left border-b border-zinc-100 whitespace-nowrap">{formatTime(log.createdAt)}</td>
                    <td className="p-4 text-left border-b border-zinc-100 font-mono text-xs">{shortUser(log.userId)}</td>
                    <td className="p-4 text-left border-b border-zinc-100">{log.event}</td>
                    <td className="p-4 text-left border-b border-zinc-100">{log.feature || '—'}</td>
                    <td className="p-4 text-left border-b border-zinc-100 max-w-[280px] truncate" title={log.path || undefined}>{log.path || '—'}</td>
                    <td className="p-4 text-left border-b border-zinc-100">
                      {log.success ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Success
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-500">
                          <XCircle className="w-3.5 h-3.5" />
                          Error
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            className="h-9 px-4 bg-white border border-zinc-200 rounded-btn text-sm text-foreground transition-colors hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-secondary-foreground">Page {page + 1} of {totalPages}</span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
            className="h-9 px-4 bg-white border border-zinc-200 rounded-btn text-sm text-foreground transition-colors hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-card p-8 w-full max-w-[640px] shadow-elevated max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-foreground mb-6 font-display">Activity Details</h2>
            <dl className="space-y-3 text-sm">
              {[
                ['Time', formatTime(selectedLog.createdAt)],
                ['User ID', selectedLog.userId],
                ['Event', selectedLog.event],
                ['Feature', selectedLog.feature || '—'],
                ['Path', selectedLog.path || '—'],
                ['Status', selectedLog.success ? 'Success' : 'Error'],
                ['IP Address', selectedLog.ipAddress || '—'],
                ['Error', selectedLog.error || '—'],
                ['Metadata', selectedLog.metadata || '—'],
                ['User Agent', selectedLog.userAgent || '—'],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-[120px_1fr] gap-3">
                  <dt className="text-secondary-foreground font-medium">{label}</dt>
                  <dd className="break-words whitespace-pre-wrap">{value}</dd>
                </div>
              ))}
            </dl>
            <button
              className="mt-6 h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-foreground text-sm font-medium rounded-btn transition-colors"
              onClick={() => setSelectedLog(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminActivityTab;
